/* Phase 251/252 — Mini Gantt timeline strip for Project Detail.
 * Visualises every task's start→due range as colour-coded bars, grouped
 * by assignee. Clash detection: overlapping bars on the same assignee
 * get a red ring + warning chip.
 *
 * Phase 252 — Bars are drag-aware:
 *   • Grab the LEFT edge (8 px) to resize start_date.
 *   • Grab the RIGHT edge (8 px) to resize due_date.
 *   • Drag the middle of the bar to move both dates by the same delta.
 *   • On drop, persists via PUT /tasks/:id; rolls back on error.
 */
import { useMemo, useState, useRef, useEffect } from "react";
import { Calendar, Warning, ArrowsClockwise } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import API from "../lib/api";
import { toast } from "sonner";
import { toLocalISODate } from "../lib/dateUtil";

const TODAY_ISO = toLocalISODate();
const ONE_DAY = 86400000;

function parseISO(s) {
  if (!s) return null;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}
function diffDays(a, b) {
  return Math.round((b - a) / ONE_DAY);
}
function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}
function fmtDay(d) {
  return d.toLocaleDateString("en-GB", { day: "numeric" });
}
function fmtMonthShort(d) {
  return d.toLocaleDateString("en-GB", { month: "short" });
}

const TIER_BAR = {
  in_house: { bg: "bg-blue-500", text: "text-white", border: "border-blue-700" },
  vendor:   { bg: "bg-purple-500", text: "text-white", border: "border-purple-700" },
  unassigned: { bg: "bg-slate-400", text: "text-white", border: "border-slate-500" },
};
const STATUS_OVERRIDES = {
  approved: { bg: "bg-emerald-500", border: "border-emerald-700" },
  completed_by_vendor: { bg: "bg-emerald-500", border: "border-emerald-700" },
};

/** Group helper — turn flat task list into [{name, type, tasks: [...]}] */
function groupTasksByAssignee(tasks) {
  const map = new Map();
  for (const t of tasks) {
    if (!t.start_date && !t.due_date) continue; // skip un-scheduled
    const name = t.assigned_to_name || "Unassigned";
    const type = t.assigned_type || (t.assigned_to_name ? "in_house" : "unassigned");
    const key = `${type}::${name}`;
    if (!map.has(key)) map.set(key, { name, type, tasks: [] });
    map.get(key).tasks.push(t);
  }
  // Sort: assigned first, unassigned last; within each, alpha
  return Array.from(map.values()).sort((a, b) => {
    if (a.type === "unassigned" && b.type !== "unassigned") return 1;
    if (b.type === "unassigned" && a.type !== "unassigned") return -1;
    return a.name.localeCompare(b.name);
  });
}

/** Detect overlapping pairs of tasks for the same assignee. Returns a Set
 * of task ids that overlap with at least one sibling. */
function findClashes(rows) {
  const clashIds = new Set();
  for (const row of rows) {
    const arr = row.tasks
      .map(t => ({
        id: t.id,
        s: parseISO(t.start_date) || parseISO(t.due_date),
        e: parseISO(t.due_date) || parseISO(t.start_date),
      }))
      .filter(x => x.s && x.e)
      .sort((a, b) => a.s - b.s);
    for (let i = 0; i < arr.length - 1; i++) {
      for (let j = i + 1; j < arr.length; j++) {
        // Overlap test: a.start < b.end && b.start < a.end
        if (arr[i].s <= arr[j].e && arr[j].s <= arr[i].e) {
          clashIds.add(arr[i].id);
          clashIds.add(arr[j].id);
        }
      }
    }
  }
  return clashIds;
}


export default function ProjectGanttStrip({ tasks = [], onTaskClick, onChange }) {
  const [collapsed, setCollapsed] = useState(false);

  // Pre-compute timeline range, rows, clashes — re-runs only when `tasks` changes
  const { rows, daysList, clashes, hasScheduledTasks } = useMemo(() => {
    const filtered = tasks.filter(t => t.start_date || t.due_date);
    const hasScheduled = filtered.length > 0;

    let dates = [];
    filtered.forEach(t => {
      const s = parseISO(t.start_date);
      const e = parseISO(t.due_date);
      if (s) dates.push(s);
      if (e) dates.push(e);
    });
    let minD, maxD;
    if (dates.length) {
      minD = new Date(Math.min(...dates.map(d => d.getTime())));
      maxD = new Date(Math.max(...dates.map(d => d.getTime())));
    } else {
      minD = new Date();
      maxD = addDays(minD, 14);
    }

    // Always include today + buffer
    const today = parseISO(TODAY_ISO);
    if (today < minD) minD = today;
    if (today > maxD) maxD = today;
    minD = addDays(minD, -2);
    maxD = addDays(maxD, 2);

    const days = diffDays(minD, maxD) + 1;
    const total = Math.max(7, Math.min(days, 90)); // 1 week min, 90 days max view

    const daysArr = [];
    for (let i = 0; i < total; i++) {
      daysArr.push(addDays(minD, i));
    }

    const rowsArr = groupTasksByAssignee(filtered);
    const clashSet = findClashes(rowsArr);

    return { rows: rowsArr, daysList: daysArr, clashes: clashSet, hasScheduledTasks: hasScheduled };
  }, [tasks]);

  // ── Phase 252 — drag state. We deliberately use refs (not state) for the
  // drag itself so multiple synthetic events in quick succession (e.g. dev
  // StrictMode double effect-mount) can't trigger duplicate PUT calls.
  // `dragActiveTick` is a tiny re-render trigger for visual highlight only.
  const gridRef = useRef(null);
  const [overrides, setOverrides] = useState({});
  const overridesRef = useRef({});
  overridesRef.current = overrides;
  const dragRef = useRef(null);
  const movedRef = useRef(false);
  const inflightRef = useRef(new Set());
  const [, setTick] = useState(0);
  const bumpTick = () => setTick(t => t + 1);

  const totalDays = daysList.length;
  const firstDay = daysList[0];

  const computeDayWidth = () => {
    if (!gridRef.current) return 0;
    const w = gridRef.current.clientWidth - 170;
    return w > 0 ? w / totalDays : 0;
  };

  const onBarPointerDown = (ev, task, mode) => {
    if (ev.button !== undefined && ev.button !== 0) return;
    ev.preventDefault();
    ev.stopPropagation();
    const dayWidth = computeDayWidth();
    if (!dayWidth) return;
    movedRef.current = false;
    dragRef.current = {
      taskId: task.id, mode,
      originalStart: task.start_date || task.due_date || "",
      originalEnd: task.due_date || task.start_date || "",
      startX: ev.clientX, dayWidth,
    };
    bumpTick();
  };

  useEffect(() => {
    const move = (ev) => {
      const drag = dragRef.current;
      if (!drag) return;
      const deltaPx = ev.clientX - drag.startX;
      const deltaDays = Math.round(deltaPx / drag.dayWidth);
      if (deltaDays === 0 && !movedRef.current) return;
      if (Math.abs(deltaPx) > 3) movedRef.current = true;
      const s = parseISO(drag.originalStart);
      const e = parseISO(drag.originalEnd);
      let ns = s, ne = e;
      if (drag.mode === "move") {
        ns = addDays(s, deltaDays);
        ne = addDays(e, deltaDays);
      } else if (drag.mode === "resize-start") {
        ns = addDays(s, deltaDays);
        if (ns > e) ns = e;
      } else if (drag.mode === "resize-end") {
        ne = addDays(e, deltaDays);
        if (ne < s) ne = s;
      }
      const fmt = (d) => toLocalISODate(d);
      setOverrides(o => ({ ...o, [drag.taskId]: { start_date: fmt(ns), due_date: fmt(ne) } }));
    };
    const up = () => {
      const drag = dragRef.current;
      if (!drag) return;
      // Synchronously clear the ref so the duplicate dev-mode invocation
      // becomes a no-op.
      dragRef.current = null;
      const wasMoved = movedRef.current;
      movedRef.current = false;
      bumpTick();
      if (!wasMoved) return;
      const patch = overridesRef.current[drag.taskId];
      if (!patch) return;
      if (inflightRef.current.has(drag.taskId)) return;
      inflightRef.current.add(drag.taskId);
      (async () => {
        try {
          await API.put(`/tasks/${drag.taskId}`, patch);
          toast.success("Schedule updated");
          onChange?.(drag.taskId, patch);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Failed to reschedule");
        } finally {
          inflightRef.current.delete(drag.taskId);
          setOverrides(o => { const c = { ...o }; delete c[drag.taskId]; return c; });
        }
      })();
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
    document.addEventListener("pointercancel", up);
    return () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
      document.removeEventListener("pointercancel", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Disable text selection during an active drag (same effect as the
  // previous gesture-tied effect, but driven by ref so we don't re-attach
  // listeners on every dragging-state change).
  useEffect(() => {
    const sync = () => {
      document.body.style.userSelect = dragRef.current ? "none" : "";
    };
    sync();
    return () => { document.body.style.userSelect = ""; };
  });

  if (!hasScheduledTasks) return null;

  // Build a sane month-divider list for the header
  const monthHeaders = [];
  let lastMonth = -1;
  daysList.forEach((d, idx) => {
    const m = d.getMonth();
    if (m !== lastMonth) {
      monthHeaders.push({ idx, label: `${fmtMonthShort(d)} ${d.getFullYear()}` });
      lastMonth = m;
    }
  });

  // Today's column index (1-based for grid)
  const todayDate = parseISO(TODAY_ISO);
  const todayCol = Math.max(0, diffDays(firstDay, todayDate));
  const showToday = todayCol >= 0 && todayCol < totalDays;

  const colTemplate = `170px repeat(${totalDays}, minmax(28px, 1fr))`;

  return (
    <div className="rounded-xl border border-border bg-card p-3 sm:p-4 mb-5" data-testid="project-gantt-strip">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Calendar size={16} weight="duotone" className="text-primary" />
          <h3 className="text-sm font-semibold font-[Chivo]">Schedule timeline</h3>
          <span className="text-[10px] text-muted-foreground">
            {rows.length} assignee{rows.length === 1 ? "" : "s"} · {totalDays} day{totalDays === 1 ? "" : "s"}
          </span>
          {clashes.size > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-[10px] font-bold" data-testid="gantt-clash-warning">
              <Warning size={10} weight="fill" /> {Math.ceil(clashes.size / 2)} clash{clashes.size <= 2 ? "" : "es"}
            </span>
          )}
        </div>
        <Button
          variant="ghost" size="sm"
          onClick={() => setCollapsed(!collapsed)}
          data-testid="gantt-toggle-btn"
        >
          <ArrowsClockwise size={12} className={`mr-1 transition-transform ${collapsed ? "" : "rotate-180"}`} />
          {collapsed ? "Show" : "Hide"}
        </Button>
      </div>

      {!collapsed && (
        <div className="overflow-x-auto pwa-no-overscroll" data-testid="gantt-scroll-container">
          <div className="min-w-[640px] relative">
            {/* Month divider header */}
            <div className="grid items-end gap-px text-[10px] text-muted-foreground border-b border-border pb-1 mb-1" style={{ gridTemplateColumns: colTemplate }}>
              <div /> {/* spacer over assignee column */}
              {daysList.map((d, idx) => {
                const isMonthStart = monthHeaders.some(h => h.idx === idx);
                return (
                  <div
                    key={`mh-${idx}`}
                    className={`text-center ${isMonthStart ? "font-bold text-foreground" : ""}`}
                    style={{ gridColumn: idx + 2 }}
                  >
                    {isMonthStart ? monthHeaders.find(h => h.idx === idx).label.split(" ")[0] : ""}
                  </div>
                );
              })}
            </div>

            {/* Day-of-month header */}
            <div
              className="grid items-end gap-px text-[10px] text-muted-foreground border-b border-border pb-1 mb-2"
              style={{ gridTemplateColumns: colTemplate }}
            >
              <div className="font-bold text-foreground text-[11px] text-left">Assignee</div>
              {daysList.map((d, idx) => {
                const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                const isToday = idx === todayCol;
                return (
                  <div
                    key={`dh-${idx}`}
                    className={`text-center ${isWeekend ? "opacity-50" : ""} ${isToday ? "text-primary font-bold" : ""}`}
                    style={{ gridColumn: idx + 2 }}
                  >
                    {fmtDay(d)}
                  </div>
                );
              })}
            </div>

            {/* Rows */}
            <div className="relative" ref={gridRef}>
              {showToday && (
                <div
                  className="absolute top-0 bottom-0 w-px bg-primary/40 z-0 pointer-events-none"
                  style={{ left: `calc(170px + (100% - 170px) * ${todayCol / totalDays} + ((100% - 170px) / ${totalDays} / 2))` }}
                  data-testid="gantt-today-line"
                />
              )}
              {rows.map((row, rIdx) => (
                <div
                  key={`row-${rIdx}`}
                  className="grid items-center gap-px py-1.5 border-b border-border/50"
                  style={{ gridTemplateColumns: colTemplate }}
                  data-testid={`gantt-row-${row.name.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <div className="text-[11px] flex items-center gap-1.5 min-w-0 pr-2">
                    <span className={`shrink-0 h-2 w-2 rounded-full ${row.type === "vendor" ? "bg-purple-500" : row.type === "unassigned" ? "bg-slate-400" : "bg-blue-500"}`} />
                    <span className="font-medium truncate">{row.name}</span>
                    <span className="text-muted-foreground text-[10px] shrink-0">{row.tasks.length}</span>
                  </div>
                  {/* Day cells (background grid) */}
                  {daysList.map((d, dIdx) => {
                    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                    const isToday = dIdx === todayCol;
                    return (
                      <div
                        key={`cell-${rIdx}-${dIdx}`}
                        className={`h-7 ${isWeekend ? "bg-muted/30" : ""} ${isToday ? "border-l border-primary/30" : ""}`}
                        style={{ gridColumn: dIdx + 2 }}
                      />
                    );
                  })}

                  {/* Bars */}
                  {row.tasks.map(t => {
                    // Apply drag overrides (optimistic)
                    const ov = overrides[t.id];
                    const sd = ov?.start_date || t.start_date;
                    const ed = ov?.due_date || t.due_date;
                    const s = parseISO(sd) || parseISO(ed);
                    const e = parseISO(ed) || parseISO(sd);
                    if (!s || !e) return null;
                    const startCol = Math.max(0, diffDays(firstDay, s));
                    const endCol = Math.min(totalDays - 1, diffDays(firstDay, e));
                    const span = Math.max(1, endCol - startCol + 1);
                    const tier = TIER_BAR[row.type] || TIER_BAR.unassigned;
                    const status = STATUS_OVERRIDES[t.status] || tier;
                    const clashing = clashes.has(t.id);
                    const isDraggingThis = dragRef.current?.taskId === t.id;
                    const taskWithOverride = ov ? { ...t, start_date: sd, due_date: ed } : t;
                    return (
                      <div
                        key={`bar-${t.id}`}
                        className={`relative h-5 rounded text-[10px] font-medium ${status.bg} ${tier.text} ${clashing ? "ring-2 ring-red-500 ring-offset-1 ring-offset-card" : `border ${status.border}`} ${isDraggingThis ? "shadow-lg z-20 brightness-110" : "hover:brightness-110"} transition-shadow group`}
                        style={{ gridColumn: `${startCol + 2} / span ${span}`, gridRow: 1, alignSelf: "center", touchAction: "none" }}
                        title={`${t.title} · ${sd || "?"} → ${ed || "?"}${clashing ? " · OVERLAPS" : ""}\nDrag middle = move · Drag edges = resize`}
                        data-testid={`gantt-bar-${t.id}`}
                      >
                        {/* Left resize handle */}
                        <div
                          onPointerDown={(ev) => onBarPointerDown(ev, taskWithOverride, "resize-start")}
                          className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize opacity-0 group-hover:opacity-100 bg-white/30 rounded-l"
                          data-testid={`gantt-bar-${t.id}-resize-start`}
                        />
                        {/* Move zone */}
                        <button
                          type="button"
                          onPointerDown={(ev) => onBarPointerDown(ev, taskWithOverride, "move")}
                          onClick={(ev) => {
                            // Suppress click if drag actually moved
                            if (movedRef.current || ov) { ev.preventDefault(); return; }
                            ev.stopPropagation();
                            onTaskClick?.(t);
                          }}
                          className="absolute left-2 right-2 top-0 bottom-0 px-1 truncate text-left cursor-grab active:cursor-grabbing"
                          data-testid={`gantt-bar-${t.id}-move`}
                        >
                          {span >= 2 ? t.title : ""}
                        </button>
                        {/* Right resize handle */}
                        <div
                          onPointerDown={(ev) => onBarPointerDown(ev, taskWithOverride, "resize-end")}
                          className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize opacity-0 group-hover:opacity-100 bg-white/30 rounded-r"
                          data-testid={`gantt-bar-${t.id}-resize-end`}
                        />
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-3 mt-3 text-[10px] text-muted-foreground flex-wrap">
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-blue-500" /> In-house</span>
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-purple-500" /> Supplier</span>
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Done</span>
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-slate-400" /> Unassigned</span>
              <span className="inline-flex items-center gap-1"><Warning size={10} weight="fill" className="text-red-500" /> Clash</span>
              <span className="ml-auto">Drag bar = reschedule · Edges = resize · Click = open</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
